import React, { useMemo } from "react";
import {
	GermanAccountingModel,
	TrSettlementModel,
} from "../../../requests_cm/gecoContractsService/types";
import { Formik } from "formik";
import { colorPaletteBlue600, textNeutralTertiary } from "../../../core/theme";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CustomSelect from "../../components/CustomSelect";
import { Spacer } from "../../components/Spacer";
import Modal from "../../components/Modal";
import { GenerateCurtailmentObjectsModalActions } from "../GenerateCurtailmentObjectsModalActions";

export interface CCOGermanyCPSubFormValues {
	accounting_model: GermanAccountingModel;
	tr_settlement_model: TrSettlementModel;
	create_ch51: boolean;
}

export interface CCOGermanyCPSubFormProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (values: CCOGermanyCPSubFormValues) => void;
	existingValues?: CCOGermanyCPSubFormValues;
	onSkip?: () => void;
}

const style = {
	container: {
		width: "580px",
		paddingLeft: "24px",
		paddingRight: "24px",
		paddingBottom: "24px",
	},
	titleIcon: {
		color: colorPaletteBlue600,
	},
	description: {
		color: textNeutralTertiary,
		fontSize: 20,
		fontWeight: 400,
		fontFamily: "Lato",
	},
	inputsContainer: {
		gap: "40px",
		display: "flex",
	},
};

export function CCOGermanyCPSubForm(props: Readonly<CCOGermanyCPSubFormProps>) {
	const initialValues = useMemo(
		() =>
			props.existingValues || {
				create_ch51: false,
				accounting_model: GermanAccountingModel.PROGNOSEMODELL,
				tr_settlement_model: TrSettlementModel.PAUSCHAL,
			},
		[props.existingValues]
	);
	return (
		<Formik
			enableReinitialize
			initialValues={initialValues}
			onSubmit={({
				tr_settlement_model,
				create_ch51,
				accounting_model,
			}) =>
				props.onSubmit({
					tr_settlement_model,
					accounting_model,
					create_ch51: create_ch51,
				})
			}
		>
			{({
				handleChange,
				submitForm,
				resetForm,
				values,
				setFieldValue,
			}) => (
				<Modal
					open={props.isOpen}
					onClose={props.onClose}
					title={"Generate Curtailment Objects"}
					sx={style.container}
					icon={
						<span style={style.titleIcon}>
							<AddCircleOutlineOutlinedIcon />
						</span>
					}
					actions={
						<GenerateCurtailmentObjectsModalActions
							onClose={props.onClose}
							onSkip={props.onSkip}
							submitForm={submitForm}
							resetForm={resetForm}
						/>
					}
				>
					<Box sx={style.description}>
						Please specify the accounting model and the TR Settlment
						model
					</Box>
					<Spacer gap={32} />
					<Box sx={style.inputsContainer}>
						<CustomSelect
							datatestId="tr_settlement_model"
							name="tr_settlement_model"
							label="TR settlement model"
							value={values.tr_settlement_model ?? ""}
							onChange={handleChange}
							touched={true}
							items={Object.values(TrSettlementModel).map(
								(val) => ({
									key: val,
									value: val,
									label: val,
								})
							)}
						/>
					</Box>
					<Spacer gap={32} />
					<Box sx={style.inputsContainer}>
						<CustomSelect
							datatestId="accounting_model"
							name="accounting_model"
							label="Accounting model"
							value={values.accounting_model ?? ""}
							onChange={handleChange}
							touched={true}
							items={Object.values(GermanAccountingModel).map(
								(val) => ({
									key: val,
									value: val,
									label: val,
								})
							)}
						/>
					</Box>
					<Spacer gap={32} />
					<FormControlLabel
						control={
							<Checkbox
								checked={values.create_ch51}
								onChange={(_, checked) =>
									setFieldValue("create_ch51", checked)
								}
								name="create_ch51"
								color="primary"
							/>
						}
						label="Generate Chapter51 if applicable"
					/>
				</Modal>
			)}
		</Formik>
	);
}
